import { toast } from 'react-toastify';

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

/**
 * Returns an array of wishlist items associated with a user's profile.
 * @param {object} currentUser - The user object.
 * @returns {array} An array of wishlist items, or an empty array if input is invalid or wishlist is missing.
 */
export const getWishlist = currentUser => {
  return (
    (currentUser && currentUser.id && currentUser?.attributes?.profile?.publicData?.wishlist) || []
  );
};

export const getLowerCaseName = string => {
  return string ? string.toLowerCase() : '';
};

export const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) 50vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

export const groupListingsByAuthor = listings => {
  return listings.reduce((grouped, listing) => {
    const authorId = listing.author.id.uuid;
    if (!grouped[authorId]) {
      grouped[authorId] = [];
    }
    grouped[authorId].push(listing);
    return grouped;
  }, {});
};
export const formatServiceName = serviceName => {
  // Handle null or undefined
  if (!serviceName) return '';

  // Common service name mappings for better display
  const serviceNameMappings = {
    // Underscore format (legacy)
    PRIORITY_OVERNIGHT: 'Priority Overnight',
    STANDARD_OVERNIGHT: 'Standard Overnight',
    FEDEX_2_DAY: 'FedEx 2 Day',
    FEDEX_EXPRESS_SAVER: 'FedEx Express Saver',
    FEDEX_GROUND: 'FedEx Ground',
    FEDEX_HOME_DELIVERY: 'FedEx Home Delivery',
    FEDEX_INTERNATIONAL_PRIORITY: 'FedEx International Priority',
    FEDEX_INTERNATIONAL_ECONOMY: 'FedEx International Economy',
    SMART_POST: 'Smart Post',
    USPS_EXPRESS: 'Express',
    USPS_PRIORITY: 'Priority',
    USPS_GROUND_ADVANTAGE: 'Ground Advantage',
    UPS_GROUND: 'UPS Ground',
    UPS_NEXT_DAY_AIR: 'UPS Next Day Air',
    UPS_NEXT_DAY_AIR_SAVER: 'UPS Next Day Air Saver',
    UPS_2ND_DAY_AIR: 'UPS 2nd Day Air',
    UPS_3_DAY_SELECT: 'UPS 3 Day Select',
    DHL_EXPRESS_WORLDWIDE: 'DHL Express Worldwide',
    DHL_EXPRESS_12_00: 'DHL Express 12:00',
    DHL_EXPRESS_9_00: 'DHL Express 9:00',
    DHL_ECONOMY_SELECT: 'DHL Economy Select',
    
    // CamelCase format (new EasyPost format)
    PRIORITYMAILINTERNATIONAL: 'Priority Mail International',
    PRIORITYMAIL: 'Priority Mail',
    PRIORITYMAILEXPRESS: 'Priority Mail Express',
    PRIORITYMAILEXPRESSINTERNATIONAL: 'Priority Mail Express International',
    EXPRESSMAILINTERNATIONAL: 'Express Mail International',
    EXPRESSMAIL: 'Express Mail',
    GROUNDADVANTAGE: 'Ground Advantage',
    FIRSTCLASS: 'First Class',
    PARCELSELECT: 'Parcel Select',
    EXPRESSWORLDWIDENONDOC: 'DHL Express Worldwide',
    DHLPACKETINTERNATIONAL: 'DHL Packet International',
    DHLPARCELINTERNATIONALDIRECT: 'DHL Parcel International Direct',
    DHLPARCELINTERNATIONALSTANDARD: 'DHL Parcel International Standard',
    EXPRESSWORLDWIDE: 'Express Worldwide',
    EXPRESSDOMESTIC: 'Express Domestic',
    ECONOMY: 'Economy',
    STANDARD: 'Standard',
    GROUND: 'Ground',
    OVERNIGHT: 'Overnight',
    TWODAY: '2 Day',
    THREEDAY: '3 Day',
  };

  // Check if we have a specific mapping
  const upperServiceName = serviceName.toUpperCase();
  if (serviceNameMappings[upperServiceName]) {
    return serviceNameMappings[upperServiceName];
  }

  // Otherwise, format it generically:
  // First, convert camelCase to spaced words
  let formatted = serviceName
    // Add space before capitals (camelCase handling)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Replace underscores with spaces
    .replace(/_/g, ' ')
    // Convert to title case
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase())
    // Handle special cases
    .replace(/Usps/g, 'USPS')
    .replace(/Ups/g, 'UPS') 
    .replace(/Dhl/g, 'DHL')
    .replace(/Fedex/g, 'FedEx');

  return formatted;
};

export const formatCarrierName = carrierName => {
  // Handle null or undefined
  if (!carrierName) return '';
  
  // Common carrier name mappings for better display
  const carrierNameMappings = {
    FEDEX: 'FedEx',
    FEDEXDEFAULT: 'FedEx',
    USPS: 'USPS',
    UPS: 'UPS',
    UPSDEFAULT: 'UPS',
    UPSDAP: 'UPS', // UPS DAP (Delivered At Place)
    DHL: 'DHL',
    DHLDEFAULT: 'DHL',
    DHLEXPRESS: 'DHL Express',
    'CANADA POST': 'Canada Post',
    'AUSTRALIA POST': 'Australia Post',
    AUSTRALIAPOST: 'Australia Post',
    CANADAPOST: 'Canada Post',
  };
  
  // Check if we have a specific mapping
  const upperCarrierName = carrierName.toUpperCase();
  if (carrierNameMappings[upperCarrierName]) {
    return carrierNameMappings[upperCarrierName];
  }
  
  // Otherwise, format it generically:
  // 1. Remove "Default" suffix if present
  // 2. Convert to title case
  // 3. Handle special cases
  return carrierName
    .replace(/Default$/i, '') // Remove "Default" suffix
    .replace(/_/g, ' ') // Replace underscores with spaces
    .toLowerCase() // Convert to lowercase first
    .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize first letter of each word
    .replace(/Usps/g, 'USPS') // Keep USPS in all caps
    .replace(/Ups/g, 'UPS') // Keep UPS in all caps
    .replace(/Dhl/g, 'DHL') // Keep DHL in all caps
    .replace(/Fedex/g, 'FedEx'); // Correct FedEx capitalization
};

/**
 * Display a toast notification based on the message type.
 *
 * @param {string} type The type of toast to display ('success', 'error', or 'info').
 * @param {string} message The message to display in the toast.
 * @param {Object} [options] Additional options for customizing the toast appearance and behavior.
 */
export const showToaster = (type, message, options = {}) => {
  const defaultOptions = {
    position: 'top-right',
    autoClose: type === 'info' ? 7500 : 6000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    ...options,
  };

  // Display the toast based on the type
  if (type === 'success') {
    toast.success(message, defaultOptions);
  } else if (type === 'error') {
    toast.error(message, defaultOptions);
  } else if (type === 'info') {
    toast.info(message, defaultOptions);
  } else {
    console.warn('showToast called with invalid type. Expected "success", "error", or "info".');
  }
};

export const removeUnits = label => {
  return label.replace(/Units/gi, '').trim();
};

/**
 * Returns the pay in total amount of a transaction.
 * @param {Object} tx - The transaction object to retrieve the pay in total from.
 * @returns {number|undefined} The pay in total amount of the transaction, or undefined if it cannot be retrieved.
 */
export const getPayInTotal = tx => {
  return tx && tx.id && tx?.attributes?.payinTotal?.amount;
};

/**
 * Divides a value by 100. If the value is null or undefined, returns 0.
 * @param {number|null|undefined} value
 * @returns {number}
 */
export const divideAndHandleNull = value => {
  return value ? value?.amount / 100 : 0;
};

export const checkIfFreeShipping = value => {
  // Handle checkbox arrays (e.g., ['true'] when checked, [] when unchecked)
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value);
};

export const capitalize = input => {
  // Handle non-string or empty input
  if (typeof input !== 'string' || input.length === 0) {
    return '';
  }

  // Capitalize first character and concatenate with rest of the string
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
};
